import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '@/api/client';
import type { Publication } from '@domain/publication/types';
import { useOptionalAuth } from './AuthContext';

interface PublicationContextValue {
  publications: Publication[];
  activePublication: Publication | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setActivePublicationId: (id: string) => void;
  createPublication: (input: {
    name: string;
    slug: string;
    defaultLocale: string;
  }) => Promise<Publication>;
}

const PublicationContext = createContext<PublicationContextValue | null>(null);
const STORAGE_KEY = 'spox.activePublicationId';

export function PublicationProvider({ children }: { children: ReactNode }) {
  const auth = useOptionalAuth();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() =>
    typeof localStorage === 'undefined' ? null : localStorage.getItem(STORAGE_KEY),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await api.listPublications();
      setPublications(items);
      if (items.length > 0) {
        setActiveId((currentId) => {
          if (items.some((item) => item.id === currentId)) return currentId;
          localStorage.setItem(STORAGE_KEY, items[0].id);
          return items[0].id;
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Wait for auth bootstrap in required mode so an unauthenticated probe does not
  // leave a sticky "API unavailable" error after login (Web/Mobile/Windows PWA).
  useEffect(() => {
    if (auth?.loading) return;
    if (auth?.authMode === 'required' && !auth.user) {
      setPublications([]);
      setError(null);
      setLoading(false);
      return;
    }
    if (auth?.bootstrapPublications) {
      const items = auth.bootstrapPublications;
      setPublications(items);
      setActiveId((currentId) => {
        if (items.some((item) => item.id === currentId)) return currentId;
        const nextId = items[0]?.id ?? null;
        if (nextId) localStorage.setItem(STORAGE_KEY, nextId);
        return nextId;
      });
      setError(null);
      setLoading(false);
      return;
    }
    void refresh();
  }, [auth?.loading, auth?.authMode, auth?.user?.id, auth?.bootstrapPublications, refresh]);

  const createPublication = useCallback(
    async (input: { name: string; slug: string; defaultLocale: string }) => {
      const { item } = await api.createPublication(input);
      setPublications((prev) => [item, ...prev]);
      setActiveId(item.id);
      localStorage.setItem(STORAGE_KEY, item.id);
      return item;
    },
    [],
  );

  const value = useMemo<PublicationContextValue>(
    () => ({
      publications,
      activePublication: publications.find((p) => p.id === activeId) ?? null,
      loading,
      error,
      refresh,
      setActivePublicationId: (id: string) => {
        setActiveId(id);
        localStorage.setItem(STORAGE_KEY, id);
      },
      createPublication,
    }),
    [publications, activeId, loading, error, refresh, createPublication],
  );

  return (
    <PublicationContext.Provider value={value}>{children}</PublicationContext.Provider>
  );
}

export function usePublicationContext(): PublicationContextValue {
  const ctx = useContext(PublicationContext);
  if (!ctx) {
    throw new Error('usePublicationContext must be used within PublicationProvider');
  }
  return ctx;
}

/** Non-throwing variant for components that may render outside PublicationProvider
 * (e.g. shell chrome exercised in isolation by tests). */
export function usePublicationContextOptional(): PublicationContextValue | null {
  return useContext(PublicationContext);
}
